import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { InputField } from "@/components/InputField/InputField";
import { LoadingOverlay } from "@/components/LoadingOverlay/LoadingOverlay";
import { Table, TableColumn } from "@/components/Table/Table";
import { classNames } from "@/utils/classNames";
import { useTableSearch } from "@/utils";
import { Order, SortParams } from "@/types/types";
import { useAppSelector } from "@/redux/hooks";

import { useGetSupportUsersQuery } from "@/pages/Private/redux/support-dashboard/support-dashboard.api";
import { supportDashboardSelector } from "@/pages/Private/redux/support-dashboard/support-dashboard.slice";

import { SupportUser } from "../schema/support-dashboard";
import { formatLastActive } from "../utils/support-dashboard.utils";
import {
	getConnectionTypeBadgeClasses,
	getFullName,
	getLinkedinConnectionBadge,
	getYesNoBadge,
} from "../utils/support-users.utils";

const BADGE_BASE_CLASSES =
	"inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium";

const useColumns = (): TableColumn[] => {
	const { t } = useTranslation();

	return useMemo(
		() => [
			{
				title: t("supportDashboard.users.columns.name"),
				uid: "name",
				orderTitle: "name",
				render: (item: SupportUser) => (
					<span className="text-sm font-medium text-gray-900">{getFullName(item)}</span>
				),
			},
			{
				title: t("supportDashboard.users.columns.company"),
				uid: "company",
				orderTitle: "company",
				render: (item: SupportUser) => (
					<span className="text-sm text-text-meta">{item.companyName || "—"}</span>
				),
			},
			{
				title: t("supportDashboard.users.columns.sendingUser"),
				uid: "isSendingUser",
				render: (item: SupportUser) => {
					const badge = getYesNoBadge(item.isSendingUser);

					return (
						<span className={classNames(BADGE_BASE_CLASSES, badge.classes)}>
							{t(badge.labelKey)}
						</span>
					);
				},
			},
			{
				title: t("supportDashboard.users.columns.linkedinConnected"),
				uid: "linkedinConnected",
				render: (item: SupportUser) => {
					const badge = getLinkedinConnectionBadge(item);

					return (
						<span className={classNames(BADGE_BASE_CLASSES, badge.classes)}>
							{t(badge.labelKey)}
						</span>
					);
				},
			},
			{
				title: t("supportDashboard.users.columns.emailConnected"),
				uid: "emailConnected",
				render: (item: SupportUser) => {
					const badge = getYesNoBadge(item.emailConnected);

					return (
						<span className={classNames(BADGE_BASE_CLASSES, badge.classes)}>
							{t(badge.labelKey)}
						</span>
					);
				},
			},
			{
				title: t("supportDashboard.users.columns.linkedinType"),
				uid: "linkedinConnectionType",
				render: (item: SupportUser) => (
					<span
						className={classNames(
							BADGE_BASE_CLASSES,
							getConnectionTypeBadgeClasses(item.linkedinConnectionType)
						)}
					>
						{t(`supportDashboard.users.connectionType.${item.linkedinConnectionType}`)}
					</span>
				),
			},
			{
				title: t("supportDashboard.users.columns.lastActive"),
				uid: "lastActiveAt",
				render: (item: SupportUser) => (
					<span className="text-sm text-text-meta whitespace-nowrap">
						{formatLastActive(item.lastActiveAt)}
					</span>
				),
			},
		],
		[t]
	);
};

export const SupportUsersTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const { page, setPage, limit, setLimit, searchValue, setSearchValue, query } = useTableSearch();
	const [filterLinkedinDisconnected, setFilterLinkedinDisconnected] = useState(false);
	const [sorting, setSorting] = useState<SortParams>({
		sortBy: "",
		sortOrder: Order.ASC,
	});

	const columns = useColumns();
	const { users, usersMeta } = useAppSelector(supportDashboardSelector);

	const { isLoading, isFetching } = useGetSupportUsersQuery({
		page,
		limit,
		searchValue: query || undefined,
		sortBy: sorting.sortBy || undefined,
		sortOrder: sorting.sortOrder,
		linkedinDisconnected: filterLinkedinDisconnected ? "true" : undefined,
	});

	const handleSorting = useCallback(
		(sortParams: SortParams) => {
			setPage(1);
			setSorting({
				sortBy: sortParams.sortBy || "",
				sortOrder: sortParams.sortOrder || Order.ASC,
			});
		},
		[setPage]
	);

	const handleChangeRowsPerPage = useCallback(
		(newLimit: number) => {
			setLimit(newLimit);
			setPage(1);
		},
		[setLimit, setPage]
	);

	useEffect(() => {
		setPage(1);
	}, [filterLinkedinDisconnected]);

	const totalPages = usersMeta ? Math.ceil(usersMeta.itemCount / limit) : 1;

	if (users.length === 0 && isLoading) {
		return <LoadingOverlay />;
	}

	return (
		<div className="w-full">
			{/* Search + Filters */}
			<div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-end">
				<div className="flex-grow max-w-md">
					<InputField
						handleChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
						name="search"
						placeholder={t("supportDashboard.users.searchPlaceholder")}
						value={searchValue}
					/>
				</div>
				<div className="flex items-center gap-6 mb-3">
					<Checkbox
						isChecked={filterLinkedinDisconnected}
						onChange={() => setFilterLinkedinDisconnected((v) => !v)}
					>
						{t("supportDashboard.users.filters.linkedinDisconnected")}
					</Checkbox>
				</div>
			</div>

			<Table
				columns={columns}
				data={users}
				handleChangeRowsPerPage={handleChangeRowsPerPage}
				handleFetchPage={setPage}
				handleSorting={handleSorting}
				hideFooter
				isFetching={isFetching}
				isLoading={isLoading}
				limit={limit}
				noDataText={t("supportDashboard.users.noData")}
				overallCount={usersMeta?.itemCount}
				page={page}
				totalPages={totalPages}
			/>
		</div>
	);
};
